import type { FragmentType } from '@/generated/gql/fragment-masking'
import { useFragment } from '@/generated/gql/fragment-masking'
import { graphql } from '@/generated/gql/gql'
import useTenant from '@/hooks/useTenant'
import { Select, FormControl, InputLabel, MenuItem, Box } from '@mui/material'

import { isEqual } from 'date-fns/isEqual'
import { parseISO } from 'date-fns/parseISO'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { formatTimeslotTime } from '../timeslotDates'

const canteenTimeslotSelectionUserDataFragment = graphql(`
  fragment CanteenTimeslotSelection_MenuDataFragmentV2 on CCMealService {
    timeslots {
      id
      startTime
      endTime
    }
  }
`)

interface TimeslotSelectionCardProps {
  timeslotData?: FragmentType<typeof canteenTimeslotSelectionUserDataFragment> | null
  arrivalTime?: string | null
  date: string
  timezone?: string | null
  onChangeTimeslot: (timeslot: string) => void
}

export function TimeslotSelectionCard({
  timeslotData: timeslotDataProp,
  arrivalTime,
  date,
  timezone,
  onChangeTimeslot,
}: TimeslotSelectionCardProps) {
  const timeslotData = useFragment(canteenTimeslotSelectionUserDataFragment, timeslotDataProp)
  const { t } = useTranslation()
  const tenant = useTenant()
  const showTimeWindow = !!tenant.features?.ccShowTimeslotWindow?.visibility

  const { timeslots: unsortedTimeslots } = timeslotData || { timeslots: [] }
  const timeslots = useMemo(
    () =>
      [...unsortedTimeslots].sort(
        (a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime(),
      ),
    [unsortedTimeslots],
  )

  const handleTimeslotSelection = (value: string) => {
    onChangeTimeslot(value)
  }

  const selectedTimeslotOption = useMemo(() => {
    if (!arrivalTime && timeslots.length === 1) return timeslots[0]
    return timeslots.find((timeslot) =>
      isEqual(
        parseISO(timeslot.startTime),
        arrivalTime === undefined
          ? new Date()
          : arrivalTime === null
            ? new Date(NaN)
            : parseISO(arrivalTime),
      ),
    )
  }, [timeslots, arrivalTime])

  return (
    <Box
      sx={{
        height: '100%',
        paddingTop: selectedTimeslotOption?.startTime ? '12px' : '0px',
      }}>
      <FormControl fullWidth sx={{ height: '100%' }}>
        <InputLabel>{t('cc:navBar.time')}</InputLabel>
        <Select
          disabled={timeslots.length <= 1 && !!selectedTimeslotOption && !!arrivalTime}
          value={selectedTimeslotOption?.startTime || ''}
          onChange={(e) => handleTimeslotSelection(e.target.value)}
          sx={{
            height: '100%',
            '& .MuiSelect-icon': {
              display: timeslots.length <= 1 && !!selectedTimeslotOption ? 'none' : 'auto',
            },
            '& .MuiSelect-select': {
              paddingY: '8.5px',
            },
          }}>
          {timeslots.map((timeslot) => (
            <MenuItem key={timeslot.id} value={timeslot.startTime}>
              {formatTimeslotTime(timeslot.startTime, timezone)}
              {showTimeWindow && ` - ${formatTimeslotTime(timeslot.endTime, timezone)}`}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  )
}
